import { v4 } from "uuid";

const clientSessionIdKey = "clientSessionId";
const lastChunkReloadAtKey = "lastChunkReloadAt";
// This tab's own copy of the department and view it chose, so two tabs can stay in two departments.
// LocalStorageService reads these first and keeps the shared copy for tabs that haven't chosen yet.
const chosenDepartmentIDKey = "chosenDepartmentID";
const chosenViewKey = "chosenView";

function _setItem<T>(key: string, value: T): void {
	try {
		const serializedValue = JSON.stringify(value);
		sessionStorage.setItem(key, serializedValue);
	} catch (error) {
		console.error(`Failed to set ${key} in sessionStorage:`, error);
	}
}

function _getItem<T>(key: string): T | null {
	try {
		const item = sessionStorage.getItem(key);
		return item ? JSON.parse(item) : null;
	} catch (error) {
		console.error(`Failed to parse ${key} from sessionStorage:`, error);
		return null;
	}
}

function _removeItem(key: string): void {
	try {
		sessionStorage.removeItem(key);
	} catch (error) {
		console.error(`Failed to remove ${key} from sessionStorage:`, error);
	}
}

function hasItem(key: string): boolean {
	return sessionStorage.getItem(key) !== null;
}

function clear(): void {
	try {
		sessionStorage.clear();
	} catch (error) {
		console.error("Failed to clear sessionStorage:", error);
	}
}

// Per-tab UUID used by the backend to distinguish concurrent sessions of the same user
// (e.g. so changes made in tab A trigger a refresh indicator in tab B).
// sessionStorage is per-tab by design, so each tab gets its own id automatically.
// `uuid`'s v4, not `crypto.randomUUID()`: the latter exists only in a secure context, and a page framed by a plain-http
// website (the public careers page embedded on a customer's site) isn't one, so every request would throw before sending.
function getOrCreateClientSessionId(): string {
	const existing = _getItem<string>(clientSessionIdKey);
	if (existing) return existing;
	const fresh = v4();
	_setItem(clientSessionIdKey, fresh);
	return fresh;
}

// When this tab last reloaded to recover from a failed lazy-chunk load (epoch ms). Per-tab, so one
// tab's reload doesn't stop another tab from recovering. Null when it never did or storage failed.
function getLastChunkReloadAt(): number | null {
	const stored = _getItem<unknown>(lastChunkReloadAtKey);
	// DEFAULT: a value this code didn't write counts as no reload yet, so it can't block recovery.
	if (typeof stored !== "number" || !Number.isFinite(stored)) return null;
	return stored;
}

function setLastChunkReloadAt(epochMs: number): void {
	_setItem(lastChunkReloadAtKey, epochMs);
}

function setChosenDepartmentID(departmentID: string): void {
	_setItem(chosenDepartmentIDKey, departmentID);
}
function getChosenDepartmentID(): string | null {
	return _getItem<string>(chosenDepartmentIDKey);
}
function removeChosenDepartmentID(): void {
	_removeItem(chosenDepartmentIDKey);
}

function setChosenView(view: string): void {
	_setItem(chosenViewKey, view);
}
function getChosenView(): unknown {
	return _getItem<unknown>(chosenViewKey);
}
function removeChosenView(): void {
	_removeItem(chosenViewKey);
}

/**
 * The Team roster's filters as URL-style parameters: each key once, with every value it holds. Built and read by
 * `components/team/teamFilterStorage.ts`, which owns what a valid snapshot is.
 */
export type TeamFilterSnapshot = Record<string, string[]>;

/**
 * Per-user values in this tab: `selfStorage.<userId>.<key>`, the same key format LocalStorageService's per-user
 * helpers use, so one tab that switches identity (impersonation, or a logout and another login) never reads the
 * previous identity's value.
 *
 * Unlike LocalStorageService's helpers, there is no unscoped legacy copy to dual-write or fall back to: session
 * storage never held per-user data without the user in its key.
 *
 * They also fail differently, on purpose: these log and then re-throw, where the generic helpers above swallow the
 * failure. Their caller reports storage failures itself (`components/team/teamFilterStorage.ts`), and a swallowed one
 * would reach no report at all.
 */
const perUserKeyPrefix = "selfStorage.";

function _userIdToUserStorageKey(userId: string, key: string): string {
	return `${perUserKeyPrefix}${userId}.${key}`;
}

/** The raw stored text, unparsed, so the caller can tell a value that is present but unreadable from an absent one. */
function _getRawItemPerUserId(userId: string, key: string): string | null {
	try {
		return sessionStorage.getItem(_userIdToUserStorageKey(userId, key));
	} catch (error) {
		console.error(`Failed to read ${key} for user ${userId} from sessionStorage:`, error);
		throw error;
	}
}

function _setItemPerUserId<T>(userId: string, key: string, value: T): void {
	try {
		sessionStorage.setItem(_userIdToUserStorageKey(userId, key), JSON.stringify(value));
	} catch (error) {
		console.error(`Failed to set ${key} for user ${userId} in sessionStorage:`, error);
		throw error;
	}
}

function _removeItemPerUserId(userId: string, key: string): void {
	try {
		sessionStorage.removeItem(_userIdToUserStorageKey(userId, key));
	} catch (error) {
		console.error(`Failed to remove ${key} for user ${userId} from sessionStorage:`, error);
		throw error;
	}
}

/** Whether `storageKey` is some user's value whose own key starts with `keyPrefix`: `selfStorage.<anyUser>.<keyPrefix>…`. */
function _isAnyUsersKeyWithPrefix(storageKey: string, keyPrefix: string): boolean {
	if (!storageKey.startsWith(perUserKeyPrefix)) return false;
	const userScopedKey = storageKey.slice(perUserKeyPrefix.length);
	const separatorIndex = userScopedKey.indexOf(".");
	return separatorIndex > 0 && userScopedKey.slice(separatorIndex + 1).startsWith(keyPrefix);
}

// One value per user and department, in this tab's session: a department never shows another's filters, an identity
// switch in the tab never shows the previous identity's, and a new tab starts clean.
const teamFiltersKeyPrefix = "teamFilters:";
const teamFiltersKey = (departmentId: string): string => `${teamFiltersKeyPrefix}${departmentId}`;

/** The raw stored text. Throws (after logging) when the browser refuses, so the caller can report it. */
function getTeamFilters(userId: string, departmentId: string): string | null {
	return _getRawItemPerUserId(userId, teamFiltersKey(departmentId));
}
/** Throws (after logging) when the browser refuses, so the caller can report it. */
function setTeamFilters(userId: string, departmentId: string, snapshot: TeamFilterSnapshot): void {
	_setItemPerUserId(userId, teamFiltersKey(departmentId), snapshot);
}
/** Throws (after logging) when the browser refuses, so the caller can report it. */
function removeTeamFilters(userId: string, departmentId: string): void {
	_removeItemPerUserId(userId, teamFiltersKey(departmentId));
}

/**
 * Removes every user's saved Team filters, for every department, from this tab. Called on logout, so the next
 * person to sign in on this tab starts from the default view. A failure is logged, like the other removals on that
 * path, so it cannot stop the logout.
 */
function removeAllTeamFilters(): void {
	try {
		// Collected first: removing while walking the indexes would shift them and skip a key.
		const teamFilterKeys: string[] = [];
		for (let index = 0; index < sessionStorage.length; index++) {
			const key = sessionStorage.key(index);
			if (key !== null && _isAnyUsersKeyWithPrefix(key, teamFiltersKeyPrefix)) teamFilterKeys.push(key);
		}
		for (const key of teamFilterKeys) sessionStorage.removeItem(key);
	} catch (error) {
		console.error("Failed to remove team filters from sessionStorage:", error);
	}
}

/**
 * SessionStorageService provides a simple interface for managing session storage.
 * Unlike LocalStorageService, sessionStorage is isolated per browser tab.
 * Avoid accessing sessionStorage directly in components - add your logic here instead and export the specific functions.
 */
export const sessionStorageService = {
	hasItem,
	clear,
	getOrCreateClientSessionId,
	getLastChunkReloadAt,
	setLastChunkReloadAt,
	setChosenDepartmentID,
	getChosenDepartmentID,
	removeChosenDepartmentID,
	setChosenView,
	getChosenView,
	removeChosenView,
	getTeamFilters,
	setTeamFilters,
	removeTeamFilters,
	removeAllTeamFilters,
};
